import {
  Dialog,
  DialogBackdropProps,
  DialogBodyProps,
  DialogContentProps,
  DialogFooterProps,
  DialogHeaderProps,
  DialogInteractOutsideEvent,
  DialogPositionerProps,
  DialogRootProps,
  Icon,
  Portal,
  Spinner,
} from '@chakra-ui/react';
import React, { FunctionComponent as FC, ReactNode, useId } from 'react';
import { MdClose } from 'react-icons/md';
import { MCenter, MFlex, MStack } from './chakra';
import { ignoreParentLayerDismiss, isToastInteraction } from './Overlays';

/*
 * Built on Chakra Dialog (v2's Modal). This wrapper deliberately keeps v2's
 * Modal prop shape as its external API — 103 of 105 modal-surface files in
 * the app route through this one component, so absorbing every v2→v3
 * behavioral rename here (rather than propagating it to every call site)
 * is the whole point of the wrapper.
 *
 * v2 prop → v3 translation, none of which is exposed to callers:
 * - `closeOnOverlayClick` → `closeOnInteractOutside` (already just a rename)
 * - `blockScrollOnMount` → `preventScroll` (already just a rename)
 * - `closeOnEsc` → `closeOnEscape` (rename)
 * - `onEsc` → `onEscapeKeyDown` (rename; v3's callback receives a
 *   KeyboardEvent, v2's took nothing, so it's called with no args)
 * - `returnFocusOnClose` → `restoreFocus` (rename)
 * - `initialFocusRef`/`finalFocusRef` (React ref objects) →
 *   `initialFocusEl`/`finalFocusEl` (functions returning an element) — v3's
 *   dialog machine reads focus targets lazily instead of holding a ref
 * - `isCentered` (boolean) → `placement` variant (`'center'` vs `'top'`) —
 *   v3 has no `isCentered` prop at all, `placement` is a recipe variant
 * - `motionPreset` values are dash-case in v3 (`slide-in-bottom`, not v2's
 *   `slideInBottom`) — translated via `MOTION_PRESET_MAP` below
 * - `size`/`scrollBehavior` are unchanged in both name and values (both are
 *   recipe variants in v3 rather than behavioral props, but callers don't
 *   need to know that)
 * - `variant` was accepted here in v2 but the Modal theme never actually
 *   defined a `variants` object, and no real call site passes one — dropped
 *   as dead code rather than ported
 *
 * v2's `ModalHeader` labelled the dialog automatically; v3 only points
 * `aria-labelledby` at a `Dialog.Title`. Rather than wrap the title in one
 * (its recipe typography would change every header's line height), the
 * root's `ids.title` is aimed at the existing `Dialog.Header`, so the dialog
 * keeps v2's accessible name — QA selects dialogs by role + name.
 *
 * `Dialog.CloseTrigger` renders no default icon in v3 (v2's
 * `ModalCloseButton` did), so one is rendered explicitly here rather than
 * pulling in the separate `MCloseButton`/`CloseButton` component — v2's
 * Modal styled its own close-X directly via the Modal theme's `closeButton`
 * slot, independent of the generic CloseButton component, and this keeps
 * that same independence. It also sets no `aria-label` (and the icon is
 * `aria-hidden`), so v2's "Close" name is restored explicitly.
 *
 * v2 closed only on a click on the overlay itself; v3 closes on any pointer
 * or focus outside the dialog's DOM. TinyMCE renders its menus and dialogs
 * into a `.tox-tinymce-aux` sink on <body>, and zag also counts any event
 * inside a same-origin iframe (like the editor's text area) as outside. So
 * picking a toolbar menu item, typing a link URL or clicking into the text
 * closed the modal. zag's `persistentElements` can't cover this: each entry
 * resolves one element, and every editor appends its own sink.
 *
 * zag also dismisses every layer registered after one that is removed, and
 * that cascade skips `closeOnInteractOutside`. A select or menu item that
 * opens a modal closes its popover in the same batch, so when the dialog's
 * layer registered first, the popover's removal closed the brand-new modal
 * (the quote header's "+ New Contact"). v2 had no such cascade and the modal
 * is controlled by `isOpen`, so it is ignored here (see
 * ./Overlays/ignoreParentLayerDismiss, shared with `MDrawer`).
 *
 * Toasts stay clickable over the modal from their own portal, so closing one
 * counted as an outside click too (see ./isToastInteraction, shared with
 * `MDrawer`).
 */

const TINYMCE_UI_SELECTOR = '.tox-tinymce, .tox-tinymce-aux';

const keepOpenForEditorsAndToasts = (event: DialogInteractOutsideEvent) => {
  // The target may belong to an iframe's document, so it isn't an instance
  // of this window's `Element`.
  const target = event.detail.target as Element;
  const frame = target.ownerDocument?.defaultView?.frameElement;
  if (
    target.closest?.(TINYMCE_UI_SELECTOR) ||
    frame?.closest(TINYMCE_UI_SELECTOR) ||
    isToastInteraction(event)
  ) {
    event.preventDefault();
  }
};

type DialogMotionPreset = NonNullable<DialogRootProps['motionPreset']>;
type DialogSize = NonNullable<DialogRootProps['size']>;
type DialogScrollBehavior = NonNullable<DialogRootProps['scrollBehavior']>;
type FocusTarget = React.RefObject<HTMLElement | null>;

const MOTION_PRESET_MAP: Record<string, DialogMotionPreset> = {
  slideInBottom: 'slide-in-bottom',
  slideInTop: 'slide-in-top',
  slideInLeft: 'slide-in-left',
  slideInRight: 'slide-in-right',
  scale: 'scale',
  none: 'none',
};

interface MCenterModalProps {
  isOpen: boolean;
  onClose: () => void;
  children?: ReactNode;
  size?: DialogSize;
  scrollBehavior?: DialogScrollBehavior;
  /** v2-style motion preset name (camelCase) — translated to v3's dash-case
   * variant value internally. */
  motionPreset?: keyof typeof MOTION_PRESET_MAP;
  /** @default true */
  isCentered?: boolean;
  /** @default true */
  closeOnOverlayClick?: boolean;
  /** @default true */
  closeOnEsc?: boolean;
  onEsc?: () => void;
  /** @default true */
  returnFocusOnClose?: boolean;
  initialFocusRef?: FocusTarget;
  finalFocusRef?: FocusTarget;
  /** @default true */
  blockScrollOnMount?: boolean;
  /** @default true */
  trapFocus?: boolean;
  renderFooter?: ((props: { onClose: () => void }) => React.ReactNode) | null;
  modalTitle?: ReactNode;
  modalOverlayProps?: DialogBackdropProps;
  /** v2 nested this under `modalContentProps.containerProps` — v3 exposes
   * the equivalent wrapping element (`Dialog.Positioner`) as its own slot. */
  modalPositionerProps?: DialogPositionerProps;
  modalHeaderProps?: DialogHeaderProps;
  modalContentProps?: DialogContentProps;
  modalBodyProps?: DialogBodyProps;
  modalFooterProps?: DialogFooterProps;
  renderModalTitleActions?: () => React.ReactNode;
  showCloseButton?: boolean;
  subTitle?: React.ReactNode;
  isLoading?: boolean;
}

const MCenterModal: FC<MCenterModalProps> = ({
  blockScrollOnMount,
  trapFocus,
  closeOnOverlayClick,
  closeOnEsc,
  onEsc,
  returnFocusOnClose,
  initialFocusRef,
  finalFocusRef,
  isCentered,
  isOpen,
  modalOverlayProps,
  modalPositionerProps,
  modalBodyProps,
  modalContentProps,
  modalFooterProps,
  modalHeaderProps,
  modalTitle = '',
  motionPreset = 'slideInBottom',
  showCloseButton = true,
  size = 'lg',
  scrollBehavior = 'inside',
  subTitle,
  onClose,
  renderFooter,
  renderModalTitleActions,
  children,
  isLoading = false,
}: MCenterModalProps) => {
  const titleId = useId();

  return (
    <Dialog.Root
      ids={{ title: titleId }}
      open={isOpen}
      preventScroll={blockScrollOnMount ?? true}
      trapFocus={trapFocus ?? true}
      closeOnInteractOutside={closeOnOverlayClick ?? true}
      onInteractOutside={keepOpenForEditorsAndToasts}
      onRequestDismiss={ignoreParentLayerDismiss}
      closeOnEscape={closeOnEsc ?? true}
      onEscapeKeyDown={onEsc ? () => onEsc() : undefined}
      restoreFocus={returnFocusOnClose ?? true}
      initialFocusEl={
        initialFocusRef ? () => initialFocusRef.current : undefined
      }
      finalFocusEl={finalFocusRef ? () => finalFocusRef.current : undefined}
      placement={isCentered === false ? 'top' : 'center'}
      motionPreset={MOTION_PRESET_MAP[motionPreset]}
      size={size}
      scrollBehavior={scrollBehavior}
      onOpenChange={(e) => {
        if (!e.open) {
          onClose();
        }
      }}
    >
      <Portal>
        <Dialog.Backdrop {...modalOverlayProps} />

        <Dialog.Positioner {...modalPositionerProps}>
          <Dialog.Content {...modalContentProps}>
            <Dialog.Header
              fontSize="lg"
              fontWeight="600"
              {...modalHeaderProps}
              id={titleId}
            >
              <MStack gap={0} w="100%">
                <MFlex alignItems="center">
                  {modalTitle}
                  {renderModalTitleActions && renderModalTitleActions()}
                </MFlex>
                {subTitle}
              </MStack>
            </Dialog.Header>
            {showCloseButton && (
              <Dialog.CloseTrigger aria-label="Close">
                <Icon size="md" asChild>
                  <MdClose />
                </Icon>
              </Dialog.CloseTrigger>
            )}
            <Dialog.Body {...modalBodyProps}>
              {isLoading ? (
                <MCenter>
                  <Spinner />
                </MCenter>
              ) : (
                children
              )}
            </Dialog.Body>
            {!!renderFooter && (
              <Dialog.Footer px="0" mx="4" {...modalFooterProps}>
                {renderFooter({ onClose })}
              </Dialog.Footer>
            )}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default MCenterModal;
